import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, MapPin, Phone, Mail } from "lucide-react";
import { waLink } from "@/lib/wa";

const requestTypes = ["Acquisto motore", "Ritiro veicolo da demolire", "Ricambi specifici", "Altra richiesta"];

const inputCls =
  "w-full border border-[#272A38] bg-[#0B0C10] px-4 py-3.5 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] transition-colors duration-300";

export default function ContactForm() {
  const [form, setForm] = useState({
    nome: "",
    telefono: "",
    tipo: requestTypes[0],
    veicolo: "",
    codice: "",
    messaggio: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const lines = [
      `Salve Delta R.E.M., sono ${form.nome || "un cliente"}.`,
      `Richiesta: ${form.tipo}.`,
      form.veicolo && `Veicolo: ${form.veicolo}.`,
      form.codice && `Codice motore: ${form.codice}.`,
      form.messaggio && `Messaggio: ${form.messaggio}`,
      form.telefono && `Potete ricontattarmi al ${form.telefono}.`,
    ].filter(Boolean);
    window.open(waLink(lines.join("\n")), "_blank", "noopener,noreferrer");
  };

  return (
    <section id="contatti" data-testid="contact-section" className="relative border-b border-[#272A38] bg-[#0B0C10] py-24">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(circle at 15% 20%, rgba(245,158,11,0.08) 0%, rgba(11,12,16,0) 55%)" }}
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="mono mb-3 text-xs uppercase tracking-[0.3em] text-[#F59E0B]">01 — Contatti</p>
          <h2 className="font-display text-5xl font-black uppercase leading-none tracking-tight text-[#F3F4F6] sm:text-6xl">
            Scrivici. <span className="text-stroke-amber italic">Rispondiamo</span> subito.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#9CA3AF]">
            Compila il modulo: il messaggio arriva direttamente sul nostro WhatsApp,
            già pronto con tutti i dettagli della tua richiesta.
          </p>

          <div className="mt-10 space-y-5" data-testid="contact-info-block">
            <a href="tel:+393510741289" data-testid="contact-phone-link" className="group flex items-center gap-4 text-[#9CA3AF] transition-colors duration-300 hover:text-[#F3F4F6]">
              <span className="flex h-11 w-11 items-center justify-center border border-[#272A38] text-[#F59E0B]">
                <Phone className="h-4 w-4" />
              </span>
              <span className="mono text-sm tracking-widest">351 074 1289</span>
            </a>
            <a href="mailto:info.deltademolizioni@gmail.com" data-testid="contact-email-link" className="group flex items-center gap-4 text-[#9CA3AF] transition-colors duration-300 hover:text-[#F3F4F6]">
              <span className="flex h-11 w-11 items-center justify-center border border-[#272A38] text-[#F59E0B]">
                <Mail className="h-4 w-4" />
              </span>
              <span className="mono text-sm tracking-wide">info.deltademolizioni@gmail.com</span>
            </a>
            <a
              href="https://maps.google.com/?q=Via+dei+Cipressi+1G+26100+Cremona"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-address-link"
              className="group flex items-center gap-4 text-[#9CA3AF] transition-colors duration-300 hover:text-[#F3F4F6]"
            >
              <span className="flex h-11 w-11 items-center justify-center border border-[#272A38] text-[#F59E0B]">
                <MapPin className="h-4 w-4" />
              </span>
              <span className="mono text-sm tracking-wide">Via dei Cipressi, 1/G — 26100 Cremona (CR)</span>
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="whatsapp-contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="border border-[#272A38] bg-[#12141C] p-6 sm:p-8 lg:col-span-7"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mono mb-2 block text-[10px] uppercase tracking-widest text-[#6B7280]">Nome e cognome *</label>
              <input data-testid="contact-form-name" required value={form.nome} onChange={set("nome")} placeholder="Mario Rossi" className={inputCls} />
            </div>
            <div>
              <label className="mono mb-2 block text-[10px] uppercase tracking-widest text-[#6B7280]">Telefono</label>
              <input data-testid="contact-form-phone" value={form.telefono} onChange={set("telefono")} placeholder="333 000 0000" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="mono mb-2 block text-[10px] uppercase tracking-widest text-[#6B7280]">Tipo di richiesta</label>
              <select data-testid="contact-form-request-type" value={form.tipo} onChange={set("tipo")} className={inputCls}>
                {requestTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mono mb-2 block text-[10px] uppercase tracking-widest text-[#6B7280]">Veicolo (marca, modello, anno)</label>
              <input data-testid="contact-form-vehicle" value={form.veicolo} onChange={set("veicolo")} placeholder="Audi A4 2.0 TDI 2017" className={inputCls} />
            </div>
            <div>
              <label className="mono mb-2 block text-[10px] uppercase tracking-widest text-[#6B7280]">Codice motore (se noto)</label>
              <input data-testid="contact-form-engine-code" value={form.codice} onChange={set("codice")} placeholder="04L100091C" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="mono mb-2 block text-[10px] uppercase tracking-widest text-[#6B7280]">Messaggio</label>
              <textarea
                data-testid="contact-form-message"
                value={form.messaggio}
                onChange={set("messaggio")}
                rows={4}
                placeholder="Descrivi la tua richiesta…"
                className={`${inputCls} resize-none`}
              />
            </div>
          </div>
          <button
            type="submit"
            data-testid="contact-form-submit-whatsapp"
            className="mt-6 flex w-full items-center justify-center gap-3 bg-[#25D366] px-6 py-4 font-display text-xl font-bold uppercase tracking-wide text-[#0B0C10] transition-colors duration-300 hover:bg-[#1EBE5B]"
          >
            <MessageCircle className="h-5 w-5" /> Invia su WhatsApp
          </button>
          <p className="mono mt-3 text-center text-[10px] uppercase tracking-widest text-[#6B7280]">
            Si apre WhatsApp con il messaggio già compilato — nessuna registrazione
          </p>
        </motion.form>
      </div>
    </section>
  );
}
